import { useState } from "react";
import { toast } from "sonner";
import { Mail, MapPin } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal, Kicker } from "@/components/Reveal";
import { Field, TextArea, Select, SubmitButton } from "@/components/FormBits";
import { submitContact } from "@/lib/api";

const TOPICS = [
  "Discuss a partnership", "Fund a pilot", "Invite Ngawilyi to a yarn",
  "Referral question", "Community consultation", "Media or speaking", "General enquiry",
];

const EMPTY = { name: "", email: "", topic: "", message: "" };

const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please add your name, email and message.");
      return;
    }
    setBusy(true);
    try {
      await submitContact(form);
      toast.success("Thanks for reaching out — we'll be in touch soon.");
      setForm(EMPTY);
    } catch {
      toast.error("Something went wrong sending that. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        kicker="Contact"
        title={"Every partnership, referral and program\nstarts with a yarn."}
        lede="Whether you're a family member, a professional, a potential partner or a community member with knowledge to share — we'd love to hear from you."
      />

      <section className="px-6 py-14 sm:py-20 bg-cream">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-14">
          <Reveal className="lg:col-span-4">
            <Kicker>Reach us</Kicker>
            <div className="space-y-6 mt-6">
              <div className="flex items-start gap-4">
                <div className="rounded-2xl bg-terracotta/15 p-3.5"><Mail className="h-5 w-5 text-terracotta" /></div>
                <div>
                  <p className="font-semibold text-charcoal">Email — the best way to reach us</p>
                  <p className="text-earth/80 text-sm mt-1" data-testid="contact-email">Ngawilyi.project@outlook.com</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="rounded-2xl bg-blush/50 p-3.5"><MapPin className="h-5 w-5 text-terracotta" /></div>
                <div>
                  <p className="font-semibold text-charcoal">Location</p>
                  <p className="text-earth/80 text-sm mt-1" data-testid="contact-location">South Australia — on the lands of the Arabana people and neighbouring Nations</p>
                </div>
              </div>
            </div>
            <div className="mt-10 rounded-[32px] bg-charcoal text-cream p-8 grain relative overflow-hidden">
              <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-terracotta/30 organic-blob blur-2xl" />
              <p className="relative font-display italic text-2xl leading-snug">"Your story belongs to you."</p>
              <p className="relative mt-4 text-sm text-cream/70 leading-relaxed">
                Anything you share with us is held privacy-first and consent-based. You choose what's shared, with whom, and how.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-8">
            <div className="rounded-[48px] bg-creamdark border border-ochre/25 p-8 sm:p-12">
              <form data-testid="contact-form" onSubmit={onSubmit} className="grid sm:grid-cols-2 gap-6">
                <Field label="Name" testid="contact-name-input" value={form.name} onChange={set("name")} required />
                <Field label="Email" testid="contact-email-input" type="email" value={form.email} onChange={set("email")} required />
                <div className="sm:col-span-2">
                  <Select label="What's this about?" testid="contact-topic-select" optional options={TOPICS} value={form.topic} onChange={set("topic")} />
                </div>
                <div className="sm:col-span-2">
                  <TextArea label="Message" testid="contact-message-textarea" rows={6} value={form.message} onChange={set("message")} required placeholder="Tell us a little about why you're reaching out…" />
                </div>
                <div className="sm:col-span-2 pt-2">
                  <SubmitButton testid="contact-submit-btn" busy={busy}>Send Message</SubmitButton>
                </div>
              </form>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="px-6 pb-16 sm:pb-20 bg-cream">
        <Reveal>
          <div className="max-w-7xl mx-auto">
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-terracotta mb-5">Looking for something specific?</p>
            <div className="flex flex-wrap gap-3">
              {[["Request Support — find your pathway", "/request-support"], ["Private Supports", "/private-supports"], ["NDIS Supports", "/ndis-supports"], ["Partner With Us", "/partner"]].map(([l, to]) => (
                <a key={to} data-testid={`contact-xlink-${to.slice(1)}`} href={to} className="rounded-full bg-creamdark border border-ochre/30 px-5 py-2.5 text-sm font-semibold text-earth transition-all duration-300 hover:border-terracotta hover:text-terracotta">
                  {l}
                </a>
              ))}
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default Contact;
